import {initializeApp} from "firebase/app";
import {getAuth, connectAuthEmulator} from "firebase/auth";
import {getFirestore, connectFirestoreEmulator} from "firebase/firestore";
import {getStorage, connectStorageEmulator} from "firebase/storage";
import {getFunctions, connectFunctionsEmulator} from "firebase/functions";

// Read Vite env statically so values are injected at build time
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as
  | string
  | undefined;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as
  | string
  | undefined;
const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as
  | string
  | undefined;
const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as
  | string
  | undefined;
const appId = import.meta.env.VITE_FIREBASE_APP_ID as string | undefined;
const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as
  | string
  | undefined;
const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as
  | string
  | undefined as string | undefined;

// Warn if required envs are missing
const required: Array<[string, string | undefined]> = [
  ["VITE_FIREBASE_API_KEY", apiKey],
  ["VITE_FIREBASE_AUTH_DOMAIN", authDomain],
  ["VITE_FIREBASE_PROJECT_ID", projectId],
  ["VITE_FIREBASE_STORAGE_BUCKET", storageBucket],
  ["VITE_FIREBASE_MESSAGING_SENDER_ID", messagingSenderId],
  ["VITE_FIREBASE_APP_ID", appId],
];
for (const [name, value] of required) {
  if (!value || String(value).trim() === "") {
    console.error(`Missing required environment variable: ${name}`);
  }
}

// Firebase config from environment variables
const firebaseConfig = {
  apiKey,
  authDomain,
  projectId,
  storageBucket,
  messagingSenderId,
  appId,
  measurementId,
} as const;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Export Firebase services
export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, functionsRegion || undefined);

// Emulator auto-connect logic
if (
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true"
) {
  // Auth Emulator
  const authPort = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT || 9099;
  connectAuthEmulator(auth, `http://localhost:${authPort}`);

  // Firestore Emulator
  const firestorePort =
    import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT || 8080;
  connectFirestoreEmulator(firestore, "localhost", Number(firestorePort));

  // Functions Emulator
  const functionsPort =
    import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5001;
  connectFunctionsEmulator(functions, "localhost", Number(functionsPort));

  // Storage Emulator
  const storagePort =
    import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_PORT || 9199;
  connectStorageEmulator(storage, "localhost", Number(storagePort));
}

export default app;
